import 'reflect-metadata';
import { PackageJsonFileValues } from '../../utils/PackageJsonFileValues';
import ClientsInitializer from '../clients/ClientsInitializer';
import { ClientsType } from '../clients/ClientsType';
import RepositoryInitializer from '../repositories/RepositoriesInitializer';
import { RepositoriesType } from '../repositories/RepositoriesType';
import ServicesInitializer from '../services/ServicesInitializer';
import { ServicesType } from '../services/ServicesType';
import { ManifestType } from '@clientos/types';
import ContainersInitializer, {
  ContainersType
} from './../containers/ContainersInitializer';
import { CommonsInitializeType } from './types';
import { ServiceCriticalInitialization } from '../services/ServicesCriticalInitialization';
import { bootstrapManager } from './bootstrapManager';
import { ManifestAdapter } from './ManifestAdapter';
import ConfigurationManager from '../configuration/ConfigurationManager';
import InitializationTokenCache from '../../services/JWeb/cache/InitializationTokenCache';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import { ClientOSError } from '../errors/ClientOSError';
import { ClientOSErrorCodes } from '../errors/ErrorCodes';
import { overridesRegistryStore, OverrideMethodsType } from '../overrides';

let services: ServicesType;
let clients: ClientsType;
let repositories: RepositoriesType;
let containers: ContainersType;

export class ModulesFactory {
  // Keys to initialize in ConfigurationManager
  private static readonly CONFIG_KEYS = [
    'navigation',
    'isNative',
    'authProviders',
    'shellFlags',
    'logger',
    'ast',
    'tenantHandlerProps',
    'login',
    'platform',
    'event',
    'overrides'
  ];

  static async setDependencies(): Promise<void> {
    bootstrapManager.setConfiguration();

    await ServicesInitializer.resolveDependencies({
      services,
      clients,
      repositories
    });

    await ContainersInitializer.resolveDependencies({
      services,
      clients,
      repositories,
      containers
    });
  }

  static async createModules(
    commonsInitializeProps: CommonsInitializeType
  ): Promise<void> {
    ConfigurationManager.initialize(
      commonsInitializeProps,
      ModulesFactory.CONFIG_KEYS
    );
    bootstrapManager.setInstantiation();

    repositories = await RepositoryInitializer.instantiate();

    ServicesInitializer.registerOverridableSingletons();
    services = await ServicesInitializer.instantiate(commonsInitializeProps);

    const {
      applicationService,
      authProviderService,
      graphQLService,
      tenantHandlerService
    } = services;

    clients = ClientsInitializer.instantiate({
      applicationService,
      authProviderService,
      graphQLService,
      tenantHandlerService
    });

    containers = ContainersInitializer.instantiate();
  }
}

export async function initializeCommons(
  manifest: ManifestType,
  options?: { overrideMethods?: OverrideMethodsType }
): Promise<CommonsInitializeType> {
  try {
    // Register overrides if provided
    if (options?.overrideMethods) {
      overridesRegistryStore.registerFromType(options.overrideMethods);
    }

    const importMapHash: string = globalThis?.commonsImportMapHash || 'empty';
    /* istanbul ignore next */
    internalLogger?.debug?.d5?.({
      className: 'Startup',
      methodName: 'initializeCommons()',
      message: 'Starting commons initialization...',
      version: PackageJsonFileValues.APPLICATION_VERSION,
      importMapHash: importMapHash
    });

    /** Phase 1: Configuration Discovery & Loading phase
     * - Manifest processing and enhancement
     */
    bootstrapManager.setConfigurationLoading();
    const commonsInitializeProps = await ManifestAdapter.ProcessToConvert(
      manifest
    );

    /** Phase 2: Instantiation Phase
     * - Module creation (services, clients, repositories, containers)
     *  */
    await ModulesFactory.createModules(commonsInitializeProps);

    /** Phase 3: Configuration Phase
     * Dependency setting and IoC container setup
     *  */
    await ModulesFactory.setDependencies();

    // Phase 4-6: Service initialization
    await ServiceCriticalInitialization.initializeServices(services);

    // TODO: this is not in the right place
    InitializationTokenCache.killCache();

    return commonsInitializeProps;
  } catch (error) {
    const normalizedError =
      error instanceof ClientOSError
        ? error
        : new ClientOSError(
            'Commons initialization failed',
            ClientOSErrorCodes.SERVICE_FAILED,
            error
          );

    const errorMessage =
      normalizedError instanceof Error
        ? normalizedError.message
        : 'Commons initialization failed';

    console.error('Commons initialization failed:', normalizedError);
    bootstrapManager.setError(
      errorMessage,
      'Commons initialization process failed'
    );
    throw normalizedError;
  }
}

export function getServices(): ServicesType {
  if (!services) throw new Error('Initialize the services first.');
  return services;
}

export function getClients(): ClientsType {
  if (!clients) throw new Error('Initialize the services first.');
  return clients;
}

export function getRepositories(): RepositoriesType {
  if (!repositories) throw new Error('Initialize the services first.');
  return repositories;
}

export function getContainers(): ContainersType {
  if (!containers) throw new Error('Initialize the services first.');
  return containers;
}

export function _getCommonsModules(): {
  services: ServicesType;
  clients: ClientsType;
  repositories: RepositoriesType;
} {
  if (!services || !clients || !repositories)
    throw new Error('Initialize the services first.');
  return { services, clients, repositories };
}
